import React, { useState, useCallback } from 'react';
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Message } from '../../stores/chatStore';
import { createApiInstance } from '../../services/api/axios-config';

interface AddToGraphButtonProps {
  message: Message;
  conversationId: string;
}

const AddToGraphButton: React.FC<AddToGraphButtonProps> = ({ message, conversationId }) => {
  const [isAddedToGraph, setIsAddedToGraph] = useState(false);
  const [statusChecked, setStatusChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const api = createApiInstance();

  const open = Boolean(anchorEl);

  // Check graph status only when user clicks the button
  const checkGraphStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get(
        `/api/v1/graph-items/messages/${message.id}/graph-status`
      );
      const status = response.data.status?.addedToGraph || false;
      setIsAddedToGraph(status);
      setStatusChecked(true);
      return status;
    } catch (err) {
      console.error('Error checking graph status:', err);
      // Silently fail - assume not added
      setIsAddedToGraph(false);
      setStatusChecked(true);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [message.id, api]);

  const handleButtonClick = async (event: React.MouseEvent<HTMLElement>) => {
    // If status hasn't been checked yet, check it first then open menu
    if (!statusChecked) {
      await checkGraphStatus();
    }
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddToGraph = async (scope: 'personal' | 'organization') => {
    handleMenuClose();
    
    try {
      setIsLoading(true);
      await api.post(
        `/api/v1/graph-items/messages/${message.id}/add-to-graph`,
        {
          conversationId,
          content: message.content,
          scope,
          metadata: {
            agentId: message.user_agent_id,
            role: message.role,
            timestamp: message.created_at,
          },
        }
      );
      
      setIsAddedToGraph(true);
      setSuccess(true);
    } catch (err) {
      setError(`Failed to add to ${scope} knowledge graph`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFromGraph = async () => {
    try {
      setIsLoading(true);
      await api.delete(
        `/api/v1/graph-items/messages/${message.id}/remove-from-graph`
      );
      setIsAddedToGraph(false);
      setSuccess(true);
    } catch (err) {
      setError('Failed to remove from knowledge graph');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Tooltip title={statusChecked && isAddedToGraph ? 'Manage in knowledge graph' : 'Add to knowledge graph'}>
        <IconButton
          size="small"
          onClick={handleButtonClick}
          disabled={isLoading}
          style={{ position: 'relative' }}
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <GraphIcon filled={statusChecked && isAddedToGraph} />
          )}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {isAddedToGraph ? (
          <MenuItem onClick={handleRemoveFromGraph}>
            <ListItemIcon>
              <RemoveCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove from Knowledge Graph</ListItemText>
          </MenuItem>
        ) : [
          <MenuItem key="personal" onClick={() => handleAddToGraph('personal')}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add to Personal Graph</ListItemText>
          </MenuItem>,
          <MenuItem key="organization" onClick={() => handleAddToGraph('organization')}>
            <ListItemIcon>
              <BusinessIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add to Organization Graph</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
      >
        <Alert severity="success" onClose={() => setSuccess(false)}>
          {isAddedToGraph
            ? 'Message added to knowledge graph'
            : 'Message removed from knowledge graph'}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

// Custom SVG icon for the 4-node open graph pattern
const GraphIcon: React.FC<{ filled?: boolean }> = ({ filled = false }) => {
  const color = filled ? '#4DC4D2' : 'currentColor';
  const fillOpacity = filled ? 1 : 0;
  
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Edges/connections - open pattern, no closed triangle */}
      <line
        x1="5"
        y1="7"
        x2="9"
        y2="17"
        stroke={color}
        strokeWidth="2"
        strokeOpacity={filled ? 0.8 : 0.5}
      />
      <line
        x1="9"
        y1="17"
        x2="15"
        y2="7"
        stroke={color}
        strokeWidth="2"
        strokeOpacity={filled ? 0.8 : 0.5}
      />
      <line
        x1="15"
        y1="7"
        x2="19"
        y2="17"
        stroke={color}
        strokeWidth="2"
        strokeOpacity={filled ? 0.8 : 0.5}
      />
      
      {/* Nodes - alternating high/low pattern */}
      <circle
        cx="5"
        cy="7"
        r="2.5"
        stroke={color}
        strokeWidth="2"
        fill={color}
        fillOpacity={fillOpacity}
      />
      <circle
        cx="9"
        cy="17"
        r="2.5"
        stroke={color}
        strokeWidth="2"
        fill={color}
        fillOpacity={fillOpacity}
      />
      <circle
        cx="15"
        cy="7"
        r="2.5"
        stroke={color}
        strokeWidth="2"
        fill={color}
        fillOpacity={fillOpacity}
      />
      <circle
        cx="19"
        cy="17"
        r="2.5"
        stroke={color}
        strokeWidth="2"
        fill={color}
        fillOpacity={fillOpacity}
      />
    </svg>
  );
};

export default AddToGraphButton;